import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const FAQSection = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  
  // FAQ categories
  const categories = [
    { id: 'all', name: 'All Questions' },
    { id: 'general', name: 'General' },
    { id: 'packages', name: 'Packages & Pricing' },
    { id: 'test', name: 'LSAT Test Prep' },
    { id: 'platform', name: 'Platform & Technical' }
  ];
  
  // FAQ items with categories
  const faqItems = [
    {
      id: 1,
      question: 'What is the LSAT and why is it important?',
      answer: 'The LSAT (Law School Admission Test) is a standardized test required for admission to most law schools in the United States and Canada. It measures skills that are considered essential for success in law school, including reading comprehension, analytical reasoning, and logical reasoning. The LSAT is important because it is one of the most significant factors in law school admissions decisions, often weighted equally with or more heavily than your undergraduate GPA.',
      category: 'general'
    },
    {
      id: 2,
      question: 'How is the LSAT scored?',
      answer: 'The LSAT is scored on a scale of 120-180, with 120 being the lowest possible score and 180 being the highest. The average score is around 150. The score is determined by the number of questions answered correctly (your raw score), which is then converted to the 120-180 scale. There is no penalty for wrong answers, so it\'s advantageous to answer every question even if you\'re unsure.',
      category: 'general'
    },
    {
      id: 3,
      question: 'How long should I study for the LSAT?',
      answer: 'Most successful test-takers spend between 3-6 months studying for the LSAT, typically dedicating 15-25 hours per week. However, the optimal study timeline varies based on your starting point, target score, and personal circumstances. Our diagnostic test can help you determine how much preparation you\'ll need to reach your target score.',
      category: 'test'
    },
    {
      id: 4,
      question: 'What\'s included in the Beginner Package?',
      answer: 'The Beginner Package is designed for those just starting their LSAT journey. It includes:\n• Extended time on practice tests to help you understand the question formats\n• Additional hints and explanations\n• Two LSAT Gain (TM) booster packs to help guide you through the exam\n\nThis package is ideal for students who are 4-6 months away from their test date and need a structured introduction to LSAT concepts.',
      category: 'packages'
    },
    {
      id: 5,
      question: 'What\'s included in the Intermediate Package?',
      answer: 'The Intermediate Package is designed for students who have some familiarity with the LSAT. It includes:\n• Standard-timed practice tests to simulate real test conditions\n• One complete test pack with full-length practice exams\n• Basic analytics to track your progress\n• One LSAT Gain (TM) booster packs to help guide you through the exam\n\nThis package is our most popular option and is recommended for students who are 2-4 months away from their test date.',
      category: 'packages'
    },
    {
      id: 6,
      question: 'What\'s included in the Expert Package?',
      answer: 'The Expert Package is our most comprehensive offering, designed for serious students aiming for top scores. It includes:\n• Real test simulation with identical conditions to the actual LSAT\n• Advanced analytics to track your progress\n• Personalized AI guidance to help you score toward the 99th percentile\n\nThis package is ideal for students targeting top-tier law schools and those who want the most comprehensive preparation experience.',
      category: 'packages'
    },
    {
      id: 7,
      question: 'How accurate are your practice tests compared to the real LSAT?',
      answer: 'Our practice tests are crafted to closely mirror the real LSAT in terms of question types, difficulty level, and timing. We regularly update our question bank based on recent LSAT administrations to ensure they remain current. Our students consistently report that our practice test scores were within 2-3 points of their actual LSAT scores. Each question is approved by a professional LSAT mentor.',
      category: 'test'
    },
    {
      id: 9,
      question: 'Is there a score improvement guarantee?',
      answer: 'We don\'t offer a specific point improvement guarantee because every student starts at a different level and progresses at their own pace. However, we do offer a satisfaction guarantee: if you complete all the recommended study materials in your package and don\'t see improvement, contact us within 30 days of your LSAT for a partial refund or extended access to continue your preparation.',
      category: 'packages'
    },
    {
      id: 11,
      question: 'Does your platform work on all devices?',
      answer: 'Yes, our platform is fully responsive and works on desktop computers, laptops, tablets, and smartphones. We recommend using a larger screen for taking practice tests to better simulate the digital LSAT experience, but all study materials and analytics are accessible on any device.',
      category: 'platform'
    },
    {
      id: 12,
      question: 'How often do you update your practice materials?',
      answer: 'We update our practice materials daily to reflect any changes in the LSAT format and to incorporate insights from recent test administrations. When significant changes occur to the LSAT (such as the transition to the digital format), we update our platform immediately to ensure our students are preparing with the most current materials.',
      category: 'platform'
    },
    {
      id: 13,
      question: 'What if I need to take a break from studying?',
      answer: 'We understand that life sometimes gets in the way of LSAT preparation. After purchasing a test, you can take the test at any time that is convienent for you.',
      category: 'packages'
    },
    // {
    //   id: 14,
    //   question: 'Do you offer tutoring or office hours with instructors?',
    //   answer: 'We offer personalized coaching with LSAT experts who scored in the 99th percentile. For Beginner and Intermediate packages, we offer add-on tutoring sessions at an additional cost. We also host weekly office hours that are accessible to all subscribers, where you can ask questions and get guidance from our instructors.',
    //   category: 'packages'
    // },
    // {
    //   id: 15,
    //   question: 'How does the free test help me prepare?',
    //   answer: 'Our free test provides a snapshot of your current LSAT readiness and identifies your strengths and weaknesses. After completing the test, you\'ll receive a score report with personalized recommendations for improvement. This helps you determine which package would be most beneficial for your preparation needs and gives you a taste of our platform\'s features.',
    //   category: 'test'
    // }
  ];
  
  // Toggle question expansion
  const toggleExpand = (id: number) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  // Filter FAQ items based on category and search query
  const filteredFAQs = faqItems.filter(item => {
    const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
    const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          item.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  
  // Auto-expand the first item when category changes
  useEffect(() => {
    if (filteredFAQs.length > 0 && Object.keys(expandedItems).length === 0) {
      setExpandedItems({ [filteredFAQs[0].id]: true });
    }
  }, [activeCategory]);
  
  return (
    <div className="bg-gradient-to-br from-white to-indigo-50 py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          {/* <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Find answers to common questions about the LSAT and our packages.
          </p> */}
        </div>
        
        {/* Search */}
        <div className="mb-10">
          <div className="max-w-xl mx-auto relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        {/* Category tabs */}
        <div className="mb-8 border-b border-gray-200">
          <div className="flex overflow-x-auto pb-1 hide-scrollbar">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => {
                  setActiveCategory(category.id);
                  setExpandedItems({});
                }}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-md mr-8 ${
                  activeCategory === category.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* FAQ accordion */}
        <div className="space-y-4">
          {filteredFAQs.length > 0 ? (
            filteredFAQs.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => toggleExpand(item.id)}
                  className="w-full px-6 py-4 text-left focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-inset"
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">{item.question}</h3>
                    {expandedItems[item.id] ? (
                      <ChevronUp className="h-5 w-5 text-indigo-600" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-indigo-600" />
                    )}
                  </div>
                </button>
                
                {expandedItems[item.id] && (
                  <div className="px-6 pb-4">
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-gray-600 whitespace-pre-line">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-10">
              <p className="text-gray-600 mb-4">No FAQs match your search criteria.</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategory('all');
                }}
                className="text-indigo-600 font-medium hover:text-indigo-500 focus:outline-none"
              >
                Clear search and show all questions
              </button>
            </div>
          )}
        </div>
        
        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 shadow-lg">
          <h3 className="text-2xl font-bold text-white mb-4">
            Still have questions?
          </h3>
          <p className="text-indigo-100 mb-6 max-w-2xl mx-auto">
            Our support team is here to help you with any questions about our platform, packages, or LSAT preparation in general.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/contact-us" 
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-indigo-600 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-indigo-600 focus:ring-white"
            >
              Contact Support
            </Link>
            <a 
              href="/free-test" 
              className="inline-flex items-center justify-center px-6 py-3 border border-white rounded-md shadow-sm text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-indigo-600 focus:ring-white"
            >
              Try Free Test <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQSection;
